/**
 * Estado de la cola de reintento.
 *
 * El encuestador necesita poder responder con certeza a una sola pregunta:
 * «¿ya llegó mi trabajo al servidor?». Esta pantalla la responde elemento por
 * elemento, en lugar de con un indicador global que obliga a confiar.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { queueSnapshot, retryFailed, syncNow, type SyncOutcome } from '../db/queue';
import type { QueueItem } from '../db/db';
import { useApp } from '../store';
import { ConnectionBadge } from './Home';

export function SyncScreen() {
  const { isOnline, refreshPending, markSynced } = useApp();
  const [snapshot, setSnapshot] = useState<Awaited<ReturnType<typeof queueSnapshot>> | null>(null);
  const [busy, setBusy] = useState(false);
  const [outcome, setOutcome] = useState<SyncOutcome | null>(null);

  const refresh = useCallback(async () => {
    setSnapshot(await queueSnapshot());
    await refreshPending();
  }, [refreshPending]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const doSync = async () => {
    setBusy(true);
    try {
      const result = await syncNow();
      setOutcome(result);
      markSynced();
      await refresh();
    } finally {
      setBusy(false);
    }
  };

  const doRetry = async () => {
    setBusy(true);
    try {
      await retryFailed();
      const result = await syncNow();
      setOutcome(result);
      await refresh();
    } finally {
      setBusy(false);
    }
  };

  const pendingTotal = (snapshot?.pending.length ?? 0) + (snapshot?.sending.length ?? 0);
  const problemTotal = (snapshot?.rejected.length ?? 0) + (snapshot?.exhausted.length ?? 0);

  return (
    <div className="app">
      <div className="topbar">
        <Link to="/" className="btn btn--ghost" style={{ minHeight: 'auto', padding: 4 }}>
          ‹ Inicio
        </Link>
        <div className="topbar__title">Sincronización</div>
        <ConnectionBadge isOnline={isOnline} />
      </div>

      <main className="app__main">
        {outcome ? (
          <div
            className={`notice ${
              outcome.rejected > 0
                ? 'notice--warning'
                : outcome.networkFailure
                  ? 'notice--info'
                  : 'notice--success'
            }`}
          >
            <span className="notice__title">
              {outcome.accepted} enviadas · {outcome.duplicates} ya estaban ·{' '}
              {outcome.rejected} rechazadas
            </span>
            {outcome.messages.map((message, i) => (
              <p key={i} style={{ margin: '4px 0 0' }}>
                {message}
              </p>
            ))}
          </div>
        ) : null}

        {pendingTotal === 0 && problemTotal === 0 ? (
          <div className="notice notice--success">
            <span className="notice__title">Todo sincronizado</span>
            No queda nada pendiente en el dispositivo.
          </div>
        ) : null}

        {pendingTotal > 0 ? (
          <>
            <h2 className="screen-title" style={{ fontSize: 18 }}>
              Pendientes de enviar ({pendingTotal})
            </h2>
            <p className="screen-sub">
              Están guardadas en el teléfono. No cierres sesión hasta que se envíen.
            </p>
            <QueueList items={[...(snapshot?.sending ?? []), ...(snapshot?.pending ?? [])]} />
          </>
        ) : null}

        {problemTotal > 0 ? (
          <>
            <h2 className="screen-title" style={{ fontSize: 18, marginTop: 24 }}>
              Con problema ({problemTotal})
            </h2>
            <p className="screen-sub">
              El servidor las rechazó o se agotaron los intentos. Muéstraselas a tu supervisor
              antes de reintentar.
            </p>
            <QueueList items={[...(snapshot?.rejected ?? []), ...(snapshot?.exhausted ?? [])]} />
            <button
              type="button"
              className="btn btn--secondary btn--block"
              onClick={() => void doRetry()}
              disabled={busy || !isOnline}
            >
              Reintentar las que fallaron
            </button>
          </>
        ) : null}

        {(snapshot?.synced.length ?? 0) > 0 ? (
          <>
            <h2 className="screen-title" style={{ fontSize: 18, marginTop: 24 }}>
              Enviadas ({snapshot!.synced.length})
            </h2>
            <p className="screen-sub">
              Confirmadas por el servidor. Se quitan de esta lista al cabo de unos días.
            </p>
            <QueueList items={snapshot!.synced} />
          </>
        ) : null}
      </main>

      <div className="actionbar">
        <button
          type="button"
          className="btn btn--primary btn--block"
          onClick={() => void doSync()}
          disabled={busy || !isOnline || pendingTotal === 0}
        >
          {busy ? 'Enviando…' : !isOnline ? 'Sin conexión' : `Enviar ${pendingTotal} pendiente(s)`}
        </button>
      </div>
    </div>
  );
}

function QueueList({ items }: { items: readonly QueueItem[] }) {
  if (items.length === 0) return null;
  return (
    <ul className="list">
      {items.map((item) => (
        <li className="card" key={item.id}>
          <div className="row row--between">
            <p className="card__title" style={{ margin: 0, fontSize: 16 }}>
              {item.summary}
            </p>
            <StatusBadge item={item} />
          </div>
          <p className="faint">
            {item.kind === 'interview' ? 'Entrevista' : 'Vivienda'} ·{' '}
            {new Date(item.createdAt).toLocaleString('es-MX', {
              day: '2-digit',
              month: 'short',
              hour: '2-digit',
              minute: '2-digit',
            })}
            {item.attempts > 0 ? ` · ${item.attempts} intento(s)` : ''}
          </p>
          {item.lastError ? (
            <p className="field__error" style={{ marginTop: 6 }}>
              {item.lastError}
            </p>
          ) : null}
        </li>
      ))}
    </ul>
  );
}

function StatusBadge({ item }: { item: QueueItem }) {
  if (item.status === 'synced') return <span className="badge badge--ok">Enviada</span>;
  if (item.status === 'sending') return <span className="badge badge--info">Enviando</span>;
  if (item.status === 'rejected') return <span className="badge badge--err">Rechazada</span>;
  return <span className="badge badge--warn">Pendiente</span>;
}
